import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@webapp/common/components/button';
import { Loader } from '@webapp/common/components/loader';
import { CssPageBlock } from '@webapp/common/components/survey-custom';
import { CDN_URL } from '@webapp/common/conf';
import { isEmbedded, isSourcePopup, isSourceWidget } from '@webapp/common/lib/ua';
import { createBackgroundStyle, createMarginStyle, SubstrateType } from '@webapp/common/lib/ui';
import { createAccountUrl } from '@webapp/common/lib/utils';
import { IndicatorPosition } from '@webapp/common/resources/survey';

import { AgreementPopup } from '@webapp/survey/src/components/agreement-popup';
import { substrateStyles } from '@webapp/survey/src/components/questions/lib';
import { ViewWrapper } from '@webapp/survey/src/components/view/wrapper';
import {
    selectInfo,
    // selectInitialLoadingOrBootstrap,
    selectShowCompletePage,
    selectShowHeader,
    selectShowHeaderInBody,
    selectShowIndicator,
    selectShowWelcomePage,
} from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { SurveyFooter } from './footer';
import { SurveyHeader } from './header';
import { SurveyIndicator } from './indicator';
import { SurveyLang } from './lang';
import { SurveyContent } from './survey-content';
import { SurveyTitle } from './survey-title';
import { WelcomeText } from './welcome-text';

import styles from './survey.css';

const DynamicAudioRecoder = dynamic(() => import('./audio-recorder').then(({ AudioRecorder }) => AudioRecorder), {
    loading: () => <Loader />,
    ssr: false,
});

const SEND_THRESHOLD = 10;

export const SurveyView: VFC = () => {
    const [headRef, setHeadRef] = useState<HTMLDivElement | null>(null);
    const {
        design,
        hideFooter,
        id,
        multiLang,
        defaultLang,
        params: {
            main: { audio, agreementText, showAgreementText, yaMetrics, yaMetricsId },
            other: { indicatorPosition },
        },
        template,
    } = useStateSelector(selectInfo);
    const surveyInnerRef = useRef<HTMLDivElement>(null);
    const lastHeightRef = useRef(0);
    const showWelcomePage = useStateSelector(selectShowWelcomePage);
    // const initialLoadingOrBootstrapState = useStateSelector(selectInitialLoadingOrBootstrap);
    const {
        brand: { headerTitleAlign, substrate },
        css,
        view: { paddingBottom, paddingLeft, paddingRight, paddingTop },
    } = design;
    const designHash = JSON.stringify(design); // TODO cheaper comparator
    const surveyMarginStyles = useMemo(
        () => createMarginStyle(paddingTop, paddingLeft, paddingBottom, paddingRight),
        [paddingBottom, paddingLeft, paddingRight, paddingTop],
    );
    const [showAgreementPopup, setShowAgreementPopup] = useState<boolean>(showAgreementText);
    const handleClosePopup = () => {
        setShowAgreementPopup(false);
    };

    useEffect(() => {
        setShowAgreementPopup(showAgreementText);
    }, [showAgreementText]);

    useEffect(() => {
        if (yaMetrics && yaMetricsId && window.ym) {
            const idAsNumber = parseInt(yaMetricsId, 10);
            if (Number.isFinite(idAsNumber)) {
                window.ym(idAsNumber, 'init', { clickmap: true, trackLinks: true, accurateTrackBounce: true });
            }
        }
    }, [yaMetrics, yaMetricsId]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const bgStyles = useMemo(() => createBackgroundStyle(design), [designHash]);

    const showIndicator = useStateSelector(selectShowIndicator);
    const showHeader = useStateSelector(selectShowHeader);
    const showCompletePage = useStateSelector(selectShowCompletePage);
    const showHeaderInBody = useStateSelector(selectShowHeaderInBody);
    const rafIf = useRef(0);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerText = css?.text;
        document.head.appendChild(style);
        return () => void document.head.removeChild(style);
    }, [css?.text]);

    useEffect(() => {
        if (window.parent) {
            const handleMutate = () => {
                if (!surveyInnerRef.current) return;
                const clientHeight = surveyInnerRef.current.clientHeight;
                if (lastHeightRef.current !== clientHeight) {
                    const diff = Math.abs(lastHeightRef.current - clientHeight);
                    const isPrevBigger = lastHeightRef.current > clientHeight;
                    const isDiffLessThanThreshold = diff <= SEND_THRESHOLD;
                    if (isPrevBigger && isDiffLessThanThreshold) {
                        // Штатная ветка throttle: уменьшение высоты меньше порога
                        // пропускаем. Раньше здесь был console.error — давал сотни
                        // тысяч событий в Sentry (FRONT-NE9).
                        console.debug(
                            'aborted because of threshold: <before>, <after>, <diff>',
                            `<${lastHeightRef.current}>`,
                            `<${clientHeight}>`,
                            `<${diff}>`,
                        );
                        return;
                    }
                    const sentHeight = surveyInnerRef.current.clientHeight;

                    window.parent?.postMessage(`[WidgetHeight-${id}]:${sentHeight}px`, '*');
                    lastHeightRef.current = sentHeight;
                }
            };
            const handleMutateRaf = () => {
                handleMutate();
                rafIf.current = window.requestAnimationFrame(handleMutateRaf);
            };
            const mo = new MutationObserver(handleMutate);
            mo.observe(surveyInnerRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            rafIf.current = window.requestAnimationFrame(handleMutateRaf);
            const updateHeightOnDomContentLoaded = () => {
                window.cancelAnimationFrame(rafIf.current);
                if (surveyInnerRef.current) {
                    window.parent?.postMessage(`[WidgetHeight-${id}]:${surveyInnerRef.current.clientHeight}px`, '*');
                }
            };
            window.addEventListener('load', updateHeightOnDomContentLoaded);
            return () => {
                mo.disconnect();
                window.cancelAnimationFrame(rafIf.current);
                window.removeEventListener('load', updateHeightOnDomContentLoaded);
            };
        }
    }, [id]);

    useEffect(() => {
        if (headRef) {
            const headerResizeObserver = new ResizeObserver(() => {
                document.body?.style?.setProperty('--survey-header-size', `${headRef.clientHeight}px`);
            });
            headerResizeObserver.observe(headRef);

            // setTimeout(() => {
            //     window.dispatchEvent(new CustomEvent('resize'));
            // }, 1000);

            return () => headerResizeObserver.unobserve(headRef);
        }
    }, [headRef]);

    useEffect(() => {
        if (hideFooter && !showIndicator) {
            document.body?.style?.setProperty('--survey-footer-size', 'calc(env(safe-area-inset-bottom, 0))');
        }
    }, [hideFooter]);

    const renderContent = () => (
        <div
            className={cn(
                CssPageBlock.SURVEY_INNER,
                styles.surveyInner,
                !(isEmbedded && !isSourcePopup && !isSourceWidget) &&
                    !hideFooter &&
                    !showIndicator &&
                    styles.surveyInnerWithFooter,
            )}
        >
            <div ref={setHeadRef}>
                {multiLang && <SurveyLang defaultLang={defaultLang} surveyId={id} />}
                {showHeader && <SurveyHeader />}
                {showHeaderInBody && <SurveyTitle inBody align={headerTitleAlign} />}
                {showWelcomePage && (
                    <WelcomeText
                        marginStyles={surveyMarginStyles}
                        styles={substrate === SubstrateType.EVERY_QUESTION ? substrateStyles : undefined}
                    />
                )}
            </div>
            <SurveyContent marginStyle={surveyMarginStyles} />
            {showIndicator && <SurveyIndicator hideFooter={hideFooter} />}
        </div>
    );

    const renderFooter = () => {
        if (
            (isEmbedded && !isSourcePopup && !isSourceWidget) ||
            (showIndicator && indicatorPosition === IndicatorPosition.BOTTOM && !showCompletePage)
        )
            return;

        return !hideFooter && <SurveyFooter paddingLeft={paddingLeft} paddingRight={paddingRight} />;
    };

    return (
        <ViewWrapper ref={surveyInnerRef}>
            {design?.brand?.backgroundVideo && (
                <video
                    id='userVideoBg'
                    className={cn(CssPageBlock.BACKGROUND_VIDEO, styles.video)}
                    src={CDN_URL + design?.brand?.backgroundVideo}
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    controlsList='nodownload'
                >
                    Your browser does not support the video tag.
                </video>
            )}
            {design?.brand?.background && (
                <div className={cn(CssPageBlock.BACKGROUND_COLOR, styles.bg)} style={bgStyles} />
            )}
            {isEmbedded ? (
                <div className={cn(styles.embeddedContentWrap, 'survey__embedded-content-wrap')}>
                    {renderContent()}
                    {renderFooter()}
                </div>
            ) : (
                <>
                    {renderContent()}
                    {renderFooter()}
                </>
            )}
            {audio && <DynamicAudioRecoder />}
            {!isEmbedded && template && (
                <Button className={styles.copyBtn} color='blue'>
                    <a href={createAccountUrl(`copy-template/${id}`)}>Использовать шаблон</a>
                </Button>
            )}
            {showAgreementPopup && Boolean(agreementText) && (
                <AgreementPopup agreementText={agreementText} handleClosePopup={handleClosePopup} />
            )}
            <div data-iframe-height='1' />
            <div id='notify' />
        </ViewWrapper>
    );
};

SurveyView.displayName = 'SurveyView';
